// ===========================================
// Assign Piece to Lesson Modal
// ===========================================
// Modal for selecting pieces to assign to a class
// Filters by instrument and program level to show relevant pieces

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import { MusicNote as MusicNoteIcon } from '@mui/icons-material';
import { usePieces, useAssignPieceToLesson } from '../../../hooks/useGamification';
import { useSnackbar } from 'notistack';
import type { Piece } from '../../../types/gamification.types';

interface AssignPieceToLessonModalProps {
  open: boolean;
  onClose: () => void;
  lessonId: string;
  instrumentId?: string | null;
  programLevelId?: string | null;
  existingPieceIds: string[];
}

export default function AssignPieceToLessonModal({
  open,
  onClose,
  lessonId,
  instrumentId,
  programLevelId,
  existingPieceIds,
}: AssignPieceToLessonModalProps) {
  const [selectedPiece, setSelectedPiece] = useState<Piece | null>(null);

  // Fetch pieces filtered by instrument and program level
  const { data: piecesData, isLoading: loadingPieces } = usePieces({
    instrumentId: instrumentId || undefined,
    programLevelId: programLevelId || undefined,
    isActive: true,
  });

  const assignMutation = useAssignPieceToLesson();
  const { enqueueSnackbar } = useSnackbar();

  // Filter out pieces already assigned to this lesson
  const availablePieces = (piecesData?.data || []).filter(
    (p) => !existingPieceIds.includes(p.id)
  );

  const handleAssign = async () => {
    if (!selectedPiece) return;

    try {
      await assignMutation.mutateAsync({
        lessonId,
        pieceId: selectedPiece.id,
      });
      enqueueSnackbar(`"${selectedPiece.title}" assigned to class`, { variant: 'success' });
      setSelectedPiece(null);
      onClose();
    } catch {
      enqueueSnackbar('Failed to assign piece to class', { variant: 'error' });
    }
  };

  const handleClose = () => {
    setSelectedPiece(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MusicNoteIcon color="primary" />
        Assign Piece to Class
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            options={availablePieces}
            loading={loadingPieces}
            value={selectedPiece}
            onChange={(_, value) => setSelectedPiece(value)}
            getOptionLabel={(option) => {
              const parts = [option.title];
              if (option.composer) parts.push(`by ${option.composer}`);
              return parts.join(' ');
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {option.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    {option.composer && (
                      <Typography variant="caption" color="text.secondary">
                        {option.composer}
                      </Typography>
                    )}
                    {option.difficulty != null && (
                      <Chip label={`Difficulty: ${option.difficulty}`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                    {option._count?.skills != null && (
                      <Chip label={`${option._count.skills} skills`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                    {option.instrument && (
                      <Chip label={option.instrument.name} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                    {option.programLevel && (
                      <Chip label={option.programLevel.name} size="small" color="secondary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search pieces"
                placeholder="Type to search..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPieces ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              existingPieceIds.length > 0 && availablePieces.length === 0
                ? 'All matching pieces are already assigned'
                : 'No pieces found'
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          {selectedPiece && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: '1px solid',
                borderColor: 'primary.light',
                borderRadius: 1,
                backgroundColor: 'action.hover',
              }}
            >
              <Typography variant="subtitle2">{selectedPiece.title}</Typography>
              {selectedPiece.composer && (
                <Typography variant="body2" color="text.secondary">
                  by {selectedPiece.composer}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                {selectedPiece._count?.skills != null && (
                  <Chip
                    label={`${selectedPiece._count.skills} skills to learn`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {selectedPiece._count?.studentPieces != null && selectedPiece._count.studentPieces > 0 && (
                  <Chip
                    label={`${selectedPiece._count.studentPieces} students already learning`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                This piece will be automatically assigned to all enrolled students.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={!selectedPiece || assignMutation.isPending}
        >
          {assignMutation.isPending ? 'Assigning...' : 'Assign to Class'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
